import { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { inject } from '@/diagnostic/injections.ts';
import { DIAGNOSE_DIAGNOSTIC } from '@/diagnostic/DiagnosticKeys.ts';
import { PROVIDE_DIAGNOSTIC, provideDiagnostic } from '@/diagnostic/DiagnosticProvider.ts';
import { useInitOnce } from '@/generic/init';
import { Optional } from '@/generic/functional';
import {
  type DiagnosticTracking,
  type DiagnosticTrackingToast,
  DiagnosticTrackingReactContext,
} from '@/diagnostic/primary/DiagnosticTrackingContext.ts';
import { useDiagnosticPageMountedTracking } from '@/diagnostic/primary/UseDiagnosticPageMountedTracking.ts';
import {
  type Diagnostic,
  DiagnosticCompleted,
  DiagnosticNotFollowed,
  DiagnosticProgress,
} from '@/diagnostic/domain/Diagnostic.ts';
import type { DiagnosticFollowUp } from '@/diagnostic/domain/DiagnosticFollowUp.ts';
import type { ApiInformationToDiagnose } from '@/diagnostic/domain/ApiInformation.ts';
import {
  clearTrackedDiagnostic,
  loadTrackedDiagnostic,
  saveTrackedDiagnostic,
} from '@/diagnostic/secondary/DiagnosticTrackingStorage.ts';

export const DiagnosticTrackingProvider = ({ children }: { children: ReactNode }) => {
  useInitOnce(PROVIDE_DIAGNOSTIC, provideDiagnostic);
  const diagnoseDiagnostic = inject(DIAGNOSE_DIAGNOSTIC);
  const location = useLocation();

  const [lastApiInformation, setLastApiInformation] = useState<Optional<ApiInformationToDiagnose>>(
    Optional.empty(),
  );
  const [diagnostic, setDiagnostic] = useState<Optional<Diagnostic>>(Optional.empty());
  const [followUp, setFollowUp] = useState<Optional<DiagnosticFollowUp>>(Optional.empty());
  const [failure, setFailure] = useState<Optional<Error>>(Optional.empty());
  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(true);
  const [reportOpen, setReportOpen] = useState(false);
  const [toastDismissed, setToastDismissed] = useState(false);

  // Tracks whether the diagnostic page is actually mounted, so a background completion only
  // pops the report open if the user is watching; otherwise the toast/indicator informs them
  // and reopening later requires an explicit action (see openReport).
  const { notifyPageMounted, isPageMounted } = useDiagnosticPageMountedTracking();

  const onDiagnostic = useCallback(
    (current: Diagnostic) => {
      if (current instanceof DiagnosticNotFollowed) {
        setFailure(Optional.of(current));
        return;
      }

      setDiagnostic(Optional.of(current));

      if (current instanceof DiagnosticCompleted) {
        setLoading(false);
        if (isPageMounted()) {
          setReportOpen(true);
        }
        clearTrackedDiagnostic();
      }
    },
    [isPageMounted],
  );

  useEffect(() => {
    loadTrackedDiagnostic().ifPresent((id) => {
      setLoading(true);
      setModalVisible(false);
      // DiagnoseComponent only mounts once the current-user loader resolves, which can happen
      // after this effect already resumed and possibly completed the tracked diagnostic. Prime
      // the mounted signal synchronously from the URL so a reload while already on the
      // diagnostic page doesn't race past its own mount notification.
      if (location.pathname === '/') {
        notifyPageMounted(true);
      }
      setFollowUp(Optional.of(diagnoseDiagnostic.resume(id, onDiagnostic)));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [diagnoseDiagnostic, onDiagnostic]);

  const start = (api: ApiInformationToDiagnose) => {
    setLastApiInformation(Optional.of(api));
    setLoading(true);
    setModalVisible(true);
    setFailure(Optional.empty());
    setToastDismissed(false);

    diagnoseDiagnostic
      .diagnose(api, onDiagnostic, saveTrackedDiagnostic)
      .then((started) => setFollowUp(Optional.of(started)))
      .catch((error: Error) => setFailure(Optional.of(error)));
  };

  const retry = () => {
    setFailure(Optional.empty());
    setLoading(true);
    followUp.ifPresent((current) => current.retry());
    followUp.ifAbsent(() => lastApiInformation.ifPresent(start));
  };

  const stop = () => {
    followUp.ifPresent((current) => current.cancel().catch(() => {}));
    setFollowUp(Optional.empty());
    setFailure(Optional.empty());
    setDiagnostic(Optional.empty());
    setLoading(false);
    clearTrackedDiagnostic();
  };

  const continueInBackground = () => setModalVisible(false);

  const reopen = () => setModalVisible(true);

  const openReport = () => setReportOpen(true);

  const closeReport = () => setReportOpen(false);

  const dismissToast = () => setToastDismissed(true);

  const diagnosticProgress = diagnostic.filter<DiagnosticProgress>(
    (d): d is DiagnosticProgress => d instanceof DiagnosticProgress,
  );
  const diagnosticCompleted = diagnostic.filter<DiagnosticCompleted>(
    (d): d is DiagnosticCompleted => d instanceof DiagnosticCompleted,
  );

  const settled = diagnosticCompleted.isPresent() || failure.isPresent();
  const toast: Optional<DiagnosticTrackingToast> =
    settled && !modalVisible && !toastDismissed
      ? Optional.of({ kind: diagnosticCompleted.isPresent() ? 'success' : 'failure' })
      : Optional.empty();

  const value: DiagnosticTracking = {
    isLoading: loading || diagnosticProgress.isPresent(),
    modalVisible,
    progress: diagnosticProgress.map((d) => d.progress),
    failure,
    diagnosticCompleted,
    reportOpen,
    toast,
    start,
    retry,
    stop,
    continueInBackground,
    reopen,
    openReport,
    closeReport,
    dismissToast,
    notifyPageMounted,
  };

  return (
    <DiagnosticTrackingReactContext.Provider value={Optional.of(value)}>
      {children}
    </DiagnosticTrackingReactContext.Provider>
  );
};
